import EntryCard from "components/timesheet/card/entry";
import { TimesheetReviewProvider } from "components/timesheet/context/review";
import {
  HeaderDescription,
  HeaderTitle,
} from "components/timesheet/review/header";
import {
  TimesheetRoot,
  TimesheetEntries,
  TimesheetLoading,
  TimesheetContent,
} from "components/timesheet/timesheet";
import { ReviewStatus } from "components/timesheet/review";
import { TIMESHEET_MYPROJECTS_REVIEW } from "@/lib/utils";
import { ReviewByDayLoader } from "./loader";
import { useParams, useSearchParams } from "react-router-dom";
import { cn, HEADER_CONTENT_PORTAL_ID, UI_ACTION_PORTAL_ID } from "utils";
import { useState } from "react";
import { useUpdateTimesheetEntry } from "components/timesheet/review/hooks/useUpdateTimesheetEntry";
// import ReviewAlert from "../review/components/review-alert";
import { DatePicker } from "components/date-picker";
import { toast } from "sonner";
import ReviewAlert from "@/modules/timesheet/reviews/review/components/review-alert";
import { useTimesheet } from "components/timesheet/context";
import { formatDateForAPI } from "utils/utils";
import { Portal } from "@radix-ui/react-portal";
import { usePortal } from "hooks/src";
import TimeSheetReviewLayout from "@/modules/timesheet/reviews/layout";
import { useEffect } from "react";
import { stateManager } from "@/lib/state-manager";
import { useMemo } from "react";
import { TimesheetDayFilters } from "./filters";

function TimesheetReviewContent() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState(null);
  const { refresh } = useTimesheet();
  const updateTimesheetMutation = useUpdateTimesheetEntry({
    onSuccess: () => {
      toast.success("Timesheet reviewed successfully");
      refresh();
    },
    onError: (error) => {
      // Handle specific error cases
      if (error.response?.status === 403) {
        toast.error(
          "Entry Not found, It may have been updated or removed recently",
        );
        window.location.reload();
      } else {
        toast.error("Failed to review timesheet");
      }
      console.error("Error updating timesheet:", error);
      throw error;
    },
  });

  async function handleUpdateTimesheet() {
    try {
      const item = {
        date: selectedEntry.date,
        approved_hours: selectedEntry.logged_hours,
        unapproved_hours: 0,
      };
      await updateTimesheetMutation.mutateAsync({
        id: selectedEntry.id,
        values: item,
      });

      setShowConfirmDialog(false);
    } catch (error) {
      // Error handling is now done in the mutation
      throw error;
    }
  }

  const headerContentPortal = usePortal({
    elementId: HEADER_CONTENT_PORTAL_ID,
  });

  return (
    <>
      <TimesheetLoading>
        <ReviewByDayLoader />
      </TimesheetLoading>
      <TimesheetRoot>
        <div className=" ">
          <div className="flex flex-col gap-6 lg:px-6 px-4">
            <div className="flex  md:items-center md:gap-6 gap-4 md:flex-row flex-col">
              <Portal container={headerContentPortal}>
                <div>
                  <div className="flex">
                    <HeaderTitle>
                      {({ day, areLogsReviewed }) => (
                        <h1
                          className={cn(
                            "text-xl font-semibold mb-1",
                            areLogsReviewed && "text-muted-foreground",
                          )}
                        >
                          {day}
                        </h1>
                      )}
                    </HeaderTitle>{" "}
                    <ReviewStatus className="text-gray-500 size-4.5 ml-2" />
                  </div>
                  <HeaderDescription className="text-sm text-muted-foreground" />
                </div>
              </Portal>
            </div>
          </div>

          <TimesheetContent
            hideIfNoEntry={true}
            className=" p-4 lg:p-6 flex gap-4.5 w-full  flex-wrap"
          >
            <TimesheetEntries>
              {({ entry }) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  href={`/${TIMESHEET_MYPROJECTS_REVIEW}/${entry.id}`}
                  isReview={true}
                  handleReviewClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedEntry(entry);
                    setShowConfirmDialog(true);
                  }}
                />
              )}
            </TimesheetEntries>
          </TimesheetContent>
        </div>
      </TimesheetRoot>

      <ReviewAlert
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleUpdateTimesheet}
        isLoading={updateTimesheetMutation.isPending}
      />
    </>
  );
}

export default function TimesheetReviewDayGrid() {
  const [searchParams, setSearchParams] = useSearchParams();

  const day = searchParams.get("date") || formatDateForAPI(new Date());

  const uiActionPortal = usePortal({
    elementId: UI_ACTION_PORTAL_ID,
  });

  const stateContext = useMemo(
    () => ({
      endpoint: "timesheet-review",
      version: "daydashboard",
    }),
    [],
  );
  const [filters, setFilters] = useState({
    _unreviewed_hours: { _eq: true },
    logged_hours: { _gt: 0 },
    approved_hours: { _null: true },
    unapproved_hours: { _null: true },
  });

  useEffect(() => {
    stateManager.init(stateContext);

    const savedState = stateManager.get(stateContext);
    if (savedState && savedState.filters) {
      setFilters(savedState.filters);
    }
  }, [stateContext]);

  useEffect(() => {
    if (filters !== null) {
      stateManager.set({
        ...stateContext,
        filters,
      });
    }
  }, [filters, stateContext]);

  function handleDayFilters(filter) {
    setFilters((prev) => ({ ...prev, ...filter }));
  }

  const defaultFilters = {
    date: { _eq: day },
  };

  return (
    <TimeSheetReviewLayout>
      <TimesheetReviewProvider
        date={day}
        filters={{ ...defaultFilters, ...filters }}
        key={JSON.stringify({ day, filters })}
      >
        <TimesheetReviewContent setSearchParams={setSearchParams} day={day} />
      </TimesheetReviewProvider>
      <Portal container={uiActionPortal} className="flex gap-3">
        <DatePicker day={day} setSearchParams={setSearchParams} />
        <TimesheetDayFilters filters={filters} setFilters={handleDayFilters} />
      </Portal>
    </TimeSheetReviewLayout>
  );
}
